import React, { useState } from "react";
import { UploadProgress } from "../../components/";
import { HandleUploadParams } from "../../components/@types/fileanalysisform";
import DragAndDropUpload from "../../components/DragAndDropUpload";
import useExitPrompt from "../../hooks/useExitPrompt";
import useSampleUpload from "../../hooks/useSampleUpload";
import GlobalUploadButton from "./GlobalUploadButton";
import GlobalUploadStatus from "./GlobalUploadStatus";

const useUploadState = () => {
  const [isFileAnalysisOpen, setIsFileAnalysisOpen] = useState(false);
  const [isUrlAnalysisOpen, setIsUrlAnalysisOpen] = useState(false);

  const setUploadState = (modal: "file" | "url", open: boolean): void => {
    if (modal === "file") {
      setIsFileAnalysisOpen(open);
    } else {
      setIsUrlAnalysisOpen(open);
    }
  };

  const closeAnalyses = (): void => {
    setIsFileAnalysisOpen(false);
    setIsUrlAnalysisOpen(false);
  };

  return {
    analysesState: {
      file: isFileAnalysisOpen,
      url: isUrlAnalysisOpen
    },
    setUploadState,
    closeAnalyses
  };
};

type GlobalUploadProps = {
  linksReloadDocument?: boolean;
};

const GlobalUpload: React.FC<GlobalUploadProps> = ({
  linksReloadDocument = false
}) => {
  const { state, uploadSamples, resetState } = useSampleUpload();
  const { analysesState, setUploadState, closeAnalyses } = useUploadState();

  useExitPrompt(state.uploading);

  const handleUpload = ({
    files,
    password,
    uploadToTicloud,
    rlcsParams,
    dynamicAnalysis,
    localOnly,
    rlcsInteractive,
    sources
  }: HandleUploadParams): void => {
    if (!files?.length) return;

    uploadSamples(
      {
        files,
        password,
        uploadToTicloud,
        rlcsParams,
        rlcsInteractive,
        dynamicAnalysis,
        localOnly,
        sources
      },
      closeAnalyses
    );
  };

  const handleCancel = (value: string): void => {
    const controller = state.controllers?.[value];
    controller?.abort();
  };

  const toggleAnalyses = (modal: "file" | "url"): void => {
    setUploadState(modal, !analysesState[modal]);
  };

  const handleClose = (): void => {
    closeAnalyses();
    resetState();
  };

  return (
    <div className="rlbs flex">
      <GlobalUploadStatus uploadStatus={state} />
      <GlobalUploadButton
        state={state}
        onUpload={handleUpload}
        analysesState={analysesState}
        toggleAnalyses={toggleAnalyses}
        closeAnalyses={handleClose}
        linksReloadDocument={linksReloadDocument}
      />
      <DragAndDropUpload onUpload={handleUpload} />
      {state.status && (
        <UploadProgress
          files={state.status}
          onCancel={handleCancel}
          elementId="upload-progress"
        />
      )}
    </div>
  );
};

export default GlobalUpload;
